// O ESTADO VAZIO DA BIBLIOTECA — `T8.23`, e o defeito era um título que contradizia o parágrafo
// que vinha logo abaixo dele.
//
// Medido em produção em 25/09/2026, deslogado, buscando "zzzqqqxxx" em https://hq-lab.vercel.app:
// a tela mostrava, uma linha abaixo da outra,
//
//   "Ainda não há histórias publicadas aqui"
//   "Nenhuma história encontrada com esse filtro. Tente outra busca."
//   [Criar o primeiro quadrinho]
//
// e havia UMA história publicada na plataforma naquele instante. O título afirma sobre A
// PRATELEIRA, o parágrafo afirma sobre O FILTRO, e as duas frases não podem ser verdadeiras ao
// mesmo tempo. O botão errava uma terceira coisa: "o primeiro" era falso, e o destino
// (`/meus-quadrinhos/novo`) é rota protegida — um visitante deslogado que clicasse caía no login.
//
// ── POR QUE ISTO É UMA FUNÇÃO PURA, E NÃO UM `? :` DENTRO DO JSX ────────────────────────────
//
// Porque o defeito não estava na renderização: estava em NÃO TER DECIDIDO qual das três coisas a
// tela está dizendo. São três estados de mundo diferentes — a prateleira está vazia / o filtro não
// achou nada / a escola da pessoa não publicou —, e cada um tem título, explicação e saída
// próprios. Decidir isso em expressão ternária aninhada é exatamente como o título e o parágrafo
// se soltaram um do outro.
//
// Aqui a decisão é um objeto só, e o teste pina a frase sem abrir navegador.

/** O mundo, no instante em que a prateleira aparece sem nada. */
export interface PorQueVazio {
  /** Quantas histórias estão publicadas na plataforma, ANTES de qualquer filtro. */
  quantosPublicados: number;
  /** O que a pessoa digitou na busca. */
  busca: string;
  /** `todas` | `minha-escola` | uma categoria. */
  filtro: string;
  /** `todos` | `finalizados` | `em-andamento`. */
  exibicao: string;
  /** Se há conta na sessão. Decide a saída, porque criar quadrinho exige conta. */
  logado: boolean;
}

export interface TelaVazia {
  titulo: string;
  explicacao: string;
  /** A saída oferecida, ou `null` quando não há nenhuma honesta. */
  acao: { rotulo: string; destino: string } | null;
  /**
   * `true` quando a saída é TIRAR O FILTRO — a tela limpa o próprio estado em vez de navegar.
   * Sem isto, "Ver tudo que está publicado" viraria um link para a rota em que a pessoa já está.
   */
  limparFiltro: boolean;
}

function plural(n: number): string {
  return n === 1 ? "1 história publicada" : `${n} histórias publicadas`;
}

/**
 * A prateleira está sem nada na tela. Por quê, e o que oferecer.
 *
 * A ordem de precedência não é arbitrária: **o que a pessoa digitou vem primeiro**, porque é o
 * único filtro que ela escreveu com as próprias mãos e é o que ela espera ver citado de volta.
 * Depois o vínculo com escola, que é o caso em que nenhum texto de busca resolveria. Depois os
 * dois filtros de clique.
 */
export function estadoVazio(m: PorQueVazio): TelaVazia {
  const termo = m.busca.trim();

  // A PRATELEIRA ESTÁ VAZIA DE VERDADE. Aqui o filtro é irrelevante — não há o que filtrar —, e
  // dizer "nenhuma história com esse texto" mandaria a pessoa tentar outra busca à toa.
  if (m.quantosPublicados === 0) {
    return {
      titulo: "Ainda não há histórias publicadas aqui",
      explicacao: "Quando uma turma publicar um quadrinho, ele aparece nesta prateleira.",
      // Deslogado a saída NÃO é criar: `/meus-quadrinhos/novo` é rota protegida. `/roteirizar` é
      // aberta e desde o T8.24 avisa, na própria tela, que guardar exige conta.
      acao: m.logado
        ? { rotulo: "Criar o primeiro quadrinho", destino: "/meus-quadrinhos/novo" }
        : { rotulo: "Escrever a minha história", destino: "/roteirizar" },
      limparFiltro: false,
    };
  }

  // DAQUI PARA BAIXO EXISTE COISA PUBLICADA, e o que esvaziou a tela foi o filtro. Toda explicação
  // carrega O NÚMERO do que existe, que é o que impede a frase de mentir de novo.
  const quantas = plural(m.quantosPublicados);
  const verTudo = { rotulo: "Ver tudo que está publicado", destino: "/biblioteca" };

  if (termo) {
    return {
      titulo: `Nenhuma história com "${termo}"`,
      explicacao: `A biblioteca tem ${quantas}, e nenhuma delas casa com esse texto. A busca olha título, autor, turma e série.`,
      acao: verTudo,
      limparFiltro: true,
    };
  }

  if (m.filtro === "minha-escola") {
    return {
      titulo: "A sua escola ainda não publicou nada",
      explicacao: `A biblioteca tem ${quantas} de outras turmas. Quem publica material da instituição é um educador dela.`,
      acao: verTudo,
      limparFiltro: true,
    };
  }

  if (m.exibicao === "finalizados") {
    return {
      titulo: "Nenhuma história marcada como pronta",
      explicacao: `Há ${quantas}, todas ainda em construção. Quem publica escolhe se o trabalho está encerrado.`,
      acao: verTudo,
      limparFiltro: true,
    };
  }

  if (m.exibicao !== "todos") {
    return {
      titulo: "Nenhuma história em construção",
      explicacao: `Há ${quantas}, e todas já foram encerradas pelos autores.`,
      acao: verTudo,
      limparFiltro: true,
    };
  }

  return {
    titulo: "Nenhuma história nessa categoria",
    explicacao: `A biblioteca tem ${quantas} em outras categorias.`,
    acao: verTudo,
    limparFiltro: true,
  };
}
